'use client';

/**
 * StartComposer
 *
 * The centered, wide chat input shown on the Meridian start screen. It is the
 * SHARED `MeridianComposer` in uncontrolled mode — same "+" menu (files,
 * screenshots, connectors, skills), same inline model picker, same attachment
 * and skill badges as an active conversation. Starting a chat therefore costs
 * the user nothing in capability.
 *
 * The textarea stays uncontrolled (driven by the shared start-screen composer)
 * so Enter-to-send is IME-safe and identical to the rest of the app.
 */

import React from 'react';
import type { FilePart } from '../../../lib/db/types.js';
import { MeridianComposer } from '../composer/MeridianComposer.js';
import type { StartScreenComposer } from './useStartScreenComposer.js';

interface StartComposerProps {
  composer: StartScreenComposer;
  isLoading: boolean;
  /** Meridian API base — uploads and the model catalog resolve from it. */
  apiBaseUrl?: string;
  /** MCP connections page — enables the "Connect apps" row in the + menu. */
  mcpConnectionsHref?: string;
  /** Inline model switcher (default true). */
  showModelPicker?: boolean;
  placeholder?: string;
}

export function StartComposer({
  composer,
  isLoading,
  apiBaseUrl,
  mcpConnectionsHref,
  showModelPicker = true,
  placeholder = 'Ask Meridian anything…',
}: StartComposerProps) {
  const { inputRef, pendingChatId, attachedSkill, clearAttachedSkill, handleSubmit, handleKeyDown } =
    composer;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>, files: FilePart[]) => {
    handleSubmit(e, files);
  };

  return (
    <MeridianComposer
      chatId={pendingChatId}
      apiBaseUrl={apiBaseUrl}
      inputRef={inputRef}
      onSubmit={onSubmit}
      onKeyDown={handleKeyDown}
      disabled={isLoading}
      placeholder={placeholder}
      rows={1}
      maxHeightPx={320}
      autoFocus
      showInviteCaret
      attachedSkill={attachedSkill}
      onClearAttachedSkill={clearAttachedSkill}
      mcpConnectionsHref={mcpConnectionsHref}
      showModelPicker={showModelPicker}
    />
  );
}
